var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();

/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Collaboration
 * (C) Copyright IBM Corp. 2020, 2021
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define('collaboration/glass/utils/ReactBridge',['underscore', 'jquery', 'react', 'react-dom', '../../lib/@waca/core-client/js/core-client/ui/core/Class', '../../lib/@waca/core-client/js/core-client/utils/BrowserUtils', '../../lib/@waca/core-client/js/core-client/utils/DateTimeUtils', '../../nls/StringResources', '../../api/sharing/ShareController', './GlassUtil'], function (_, $, React, ReactDOM, Class, BrowserUtils, DateTimeUtils, StringResources, ShareController, GlassUtil) {

	'use strict';

	var feature = GlassUtil.feature;


	var ReactBridge = Class.extend( /** @lends ReactBridge */{
		/**
   * @desc Constructor for ReactBridge.
   * @constructs ReactBridge
   * @extends Class
   * @public
   * @param options {Object} Options
   * @param options.glassContext {Object} The glass context
   * @param options.embedVisible {boolean} False when asset is not saved yet
   * @param options.features {Array} Array of strings for user features
   * @param options.selectedAsset {Object} Current selected asset
   * @param options.selectedAsset.link {Object} Link info
   * @param options.selectedAsset.link.shareUrl {string} Asset's link
   * @param options.selectedAsset.link.embedUrl {string} Asset's embeded link
   * @param options.selectedAsset.type {Object} Asset info
   * @param options.selectedAsset.type.assetType {string} Asset's type
   * @param options.selectedAsset.type.assetTitle {string} Asset's title
   * @param options.selectedAsset.object {Object} Asset's object
   * @param options.selectedAsset.object.objectType {string} Object type
   * @param options.selectedAsset.object.objectId {string} Object Id
   * @param options.onClose {function} Optional callback when the dialog is closed
   * @param options.logger {Object} Optional logger
   */
		init: function init(_ref) {
			var glassContext = _ref.glassContext,
			    embedVisible = _ref.embedVisible,
			    features = _ref.features,
			    selectedAsset = _ref.selectedAsset,
			    onClose = _ref.onClose,
			    logger = _ref.logger;

			this.glassContext = glassContext;
			this.features = features;
			this.shareController = new ShareController({
				glassContext: glassContext,
				type: selectedAsset ? selectedAsset.type : null,
				errorHandler: this._displayError.bind(this),
				slideout: {
					hide: function hide() {} // to keep api compatibility with StoryTelling
				}
			});
			this.embedVisible = this._isEmbedEnabled() && !!embedVisible;
			this.imageVisible = this._isPlatformImageEnabled();
			this.onClose = onClose;
			this.logger = logger;

			if (selectedAsset) {
				this.link = selectedAsset.link;
				this.type = selectedAsset.type;
				this.object = selectedAsset.object;
			}

			try {
				this.instrumentationService = glassContext.getCoreSvc('.Instrumentation');
			} catch (e) {
				// just swallow it if the instrumentation service isn't available for some reason.
				void e;
			}

			this.reactContainer = null;
			this.refPanel = null;
			this.shareStore = null;
			this.imageStore = null;
		},

		execute: function execute() {
			var _this = this;

			return new Promise(function (resolve, reject) {
				require(['collaboration/canvaseditor/CanvasEditor', 'collaboration-ui/collaboration-ui.min', 'ckeditor'], function (CanvasEditor, CollaborationUI) {
					Promise.all([_this._getConfig(), _this._getConnectors(), _this._canExportToPDF(), _this._refreshLink(), _this.shareController.enterShareState()]).then(function (_ref2) {
						var _ref3 = _slicedToArray(_ref2, 4),
						    config = _ref3[0],
						    connectors = _ref3[1],
						    canExport = _ref3[2],
						    link = _ref3[3];

						var showIncludeLink = _this._isPlatformLinkEnabled();
						_this._createStores(CollaborationUI.ShareStore, CollaborationUI.ImageStore, CanvasEditor, connectors, config, canExport, link, showIncludeLink);
						_this._setShowIncludeImage();
						_this._renderReact(CollaborationUI.CollaborationPanel);
						resolve();
					}).catch(reject);
				});
			});
		},

		/**
   * Fetch user's configured product locale and Product version.
   * @instance
   * @returns {Promise} with the user's browser language.
   */
		_getConfig: function _getConfig() {
			// The passed in version number is of the form "11.1 RX", but the knowledge center
			// uses the form "11.1.X". Until glass provides consistent versioning with the knowlege
			// center, we will hard-code the version here.
			return this.glassContext.getSvc('.UserProfile').then(function (userProfile) {
				var language = userProfile.preferences.productLocale ? userProfile.preferences.productLocale : 'en';
				return {
					language: language,
					version: '11.2.0'
				};
			});
		},

		_getConnectors: function _getConnectors() {
			var _this2 = this;

			// don't need to load connectors if cannot send
			if (!this._canSend()) {
				return Promise.resolve([]);
			}

			return this.shareController.getConnectors().catch(function (error) {
				if (_this2.logger) {
					_this2.logger.error('Error fetching connectors: ' + error);
				}
				_this2.glassContext.showToast(StringResources.get('error_retrieving_platforms'), {
					type: 'error'
				});
				throw error;
			});
		},

		_canSend: function _canSend() {
			return this._isSendEnabled() && (!this.link ? true : this._checkLinkShareable(this.link));
		},

		_isSendEnabled: function _isSendEnabled() {
			return this.features.includes(feature.SEND);
		},

		_checkLinkShareable: function _checkLinkShareable(link) {
			var pathQuery = 'pathRef';
			if (!!this.type && this.type.assetType === 'folder') {
				pathQuery = 'folder';
			}
			return !!(link && link.shareUrl && link.shareUrl.search(pathQuery + '=.my_folders') === -1);
		},

		/**
   * Returns if the content supports export to pdf
   * @returns Promise
   */
		_canExportToPDF: function _canExportToPDF() {
			var _this3 = this;

			return this.shareController.canExportToPDF(this.glassContext).then(function (result) {
				// when link is set, we were called from My/Team Content navigation.
				return result && !_this3.link && _this3.features.includes(feature.EXPORT);
			});
		},

		/**
   * Refresh the current perspective's link URL.
   * @instance
   * @returns {Promise} resolved as an object that contains shareUrl and embedUrl
   */
		_refreshLink: function _refreshLink() {
			var _this4 = this;

			return new Promise(function (resolve, reject) {
				if (_this4.link) {
					_this4._setLink(_this4.link);
					resolve(_this4.link);
				} else {
					_this4.shareController.getLink(_this4.glassContext).then(function (link) {
						_this4._setLink(link);
						resolve(link);
					}).catch(function (error) {
						_this4._setLink(null);
						reject(error);
					});
				}
			});
		},

		/**
   * Returns whether "Include image" option should be visible or hidden.
   *
   * @returns {Promise} that resolves to a boolean value.
   */
		_canCaptureImage: function _canCaptureImage() {
			if (!this.imageVisible) {
				return Promise.resolve(false);
			}

			// Authoring perspective supports image capture but requires an extra check
			// of the current report format. Image capture should be enabled for HTML reports;
			// disabled for PDF reports.
			return this.shareController.canCaptureImage(this.glassContext);
		},

		/**
   * Capture the current perspective's screenshot image.
   * @instance
   * @returns {Promise} with an image string as URL.
   */
		_loadImage: function _loadImage() {
			var _this5 = this;

			return new Promise(function (resolve, reject) {
				if (_this5.refPanel && _this5.refPanel.state.connector) {
					if (_this5.refPanel.state.connector.isImageSupported()) {
						_this5.shareController.getScreenshot(_this5.glassContext).then(function (item) {
							var img = new Image();
							img.onload = function () {
								item.width = img.width;
								item.height = img.height;
								resolve(item);
							};
							img.onerror = function () {
								var error = new Error(StringResources.get('error_no_screenshot'));
								_this5.glassContext.showToast(StringResources.get('error_no_screenshot'), {
									type: 'error'
								});
								reject(error);
							};
							img.src = item.image;
						}).catch(function (error) {
							_this5.glassContext.showToast(StringResources.get('error_no_screenshot'), {
								type: 'error'
							});
							reject(error);
						});
					} else {
						var errorTxt = StringResources.get('error_screenshot_unsupported');
						_this5.glassContext.showToast(errorTxt, {
							type: 'warning'
						});
						reject(new Error(errorTxt));
					}
				} else {
					// We're not trying to load a screenshot yet.
					reject(new Error(StringResources.get('error_no_screenshot')));
				}
			});
		},

		_isLinkShareable: function _isLinkShareable(link) {
			return this._isLinkEnabled() && this._checkLinkShareable(link);
		},

		_isLinkEnabled: function _isLinkEnabled() {
			return this.features.includes(feature.LINK);
		},

		_isEmbedEnabled: function _isEmbedEnabled() {
			return this.features.includes(feature.EMBED);
		},

		_isPlatformLinkEnabled: function _isPlatformLinkEnabled() {
			return this.features.includes(feature.PLATFORMS_LINK);
		},

		_isPlatformImageEnabled: function _isPlatformImageEnabled() {
			return this.features.includes(feature.PLATFORMS_IMAGE);
		},

		_isEmailLinkEnabled: function _isEmailLinkEnabled() {
			return this.features.includes(feature.EMAIL_LINK) && this.glassContext.hasCapability('canIncludeLinkInEmail');
		},

		/**
   * Sets the current perspective's link URL.
   * @param link
   */
		_setLink: function _setLink(link) {
			if (this.shareStore) {
				this.shareStore.setLink(link);
			}
		},

		_isDirty: function _isDirty() {
			return this.glassContext.currentAppView.isDirty();
		},

		_createStores: function _createStores(ShareStore, ImageStore, CanvasEditor, connectors, config, canExport, link, showIncludeLink) {
			var asset = this.shareController.getAssetStrings();
			var shareStore = ShareStore.create({
				assetTitle: asset.assetTitle,
				assetType: asset.assetType,
				isReady: false,
				isIE11: BrowserUtils.isIE11(),
				language: config.language,
				version: config.version,
				isDirty: this._isDirty(),
				objectType: this.object && this.object.objectType || null,
				objectId: this.object && this.object.objectId || null,
				showIncludeLink: showIncludeLink
			}, {
				CanvasEditor: CanvasEditor,
				glassContext: this.glassContext,
				isLinkShareable: this._isLinkShareable.bind(this),
				send: this._send.bind(this),
				generatePDF: this._generatePDF.bind(this),
				displayError: this._displayError.bind(this),
				instrument: this._instrument.bind(this),
				features: this.features,
				glassFeature: feature,
				dateTimeUtils: DateTimeUtils,
				$root: $(document.body)
			});
			shareStore.enableSend(this._isSendEnabled());
			shareStore.enableLink(this._isLinkEnabled());
			shareStore.enableEmailLink(this._isEmailLinkEnabled());
			shareStore.enableEmbed(this._isEmbedEnabled());
			shareStore.enableExport(canExport);
			shareStore.setConnectors(connectors);
			shareStore.setLink(link);
			shareStore.setLinkEnabled(this._isLinkShareable(link));
			this.shareStore = shareStore;

			var imageStore = ImageStore.create({
				showIncludeImage: false,
				includeImage: true,
				image: '',
				imageLoaded: false,
				imageLabel: '',
				imageWidth: 0,
				imageHeight: 0
			}, {
				loadImage: this._loadImage.bind(this)
			});
			this.imageStore = imageStore;
		},

		_setShowIncludeImage: function _setShowIncludeImage() {
			var _this6 = this;

			return this._canCaptureImage().then(function (showIncludeImage) {
				_this6.imageStore.setShowIncludeImage(showIncludeImage);
				_this6.shareStore.setReady(true);
			});
		},

		_renderReact: function _renderReact(CollaborationPanel) {
			var options = {
				shareStore: this.shareStore,
				imageStore: this.imageStore,
				nls: StringResources.get,
				panel: 'main',
				cancel: this._close.bind(this),
				embedVisible: this.embedVisible,
				contentMenuShare: !!this.link
			};

			// render the view with options
			this.reactContainer = $('<div/>').appendTo(document.body).get(0);
			this._createReactPanel(CollaborationPanel, options);
		},

		_createReactPanel: function _createReactPanel(CollaborationPanel, options) {
			this.refPanel = ReactDOM.render(React.createElement(CollaborationPanel, options), this.reactContainer);
		},

		/**
   * Close the panel.
   * @instance
   * @returns {Promise}
   */
		_close: function _close() {
			var _this7 = this;

			return this.shareController.close().then(function () {
				_this7._remove();
				return _this7.shareController.leaveShareState();
			});
		},

		/**
   * Cleaning up events
   * @override
   */
		_remove: function _remove() {
			if (this.reactContainer) {
				ReactDOM.unmountComponentAtNode(this.reactContainer);
				$(this.reactContainer).remove();
				this.reactContainer = null;
				if (typeof this.onClose === 'function') {
					this.onClose();
				}
			}
		},

		/**
   * Sends the message.
   * @instance
   * @param {object} payload
   * @param {object} payload.connector
   * @param {object} payload.data
   * @param {string} payload.type
   * @returns {Promise}
   */
		_send: function _send(_ref4) {
			var _this8 = this;

			var connector = _ref4.connector,
			    data = _ref4.data,
			    type = _ref4.type;

			return this.shareController.send(connector, data).then(function (result) {
				// success
				void result;
				var message = type === 'email' ? StringResources.get('toast_success_email') : StringResources.get('toast_success', {
					connector: connector.getLabel()
				});
				_this8.glassContext.showToast(message, { type: 'success' });
			}).then(function () {
				return _this8._close();
			});
		},

		_generatePDF: function _generatePDF(pageSize, includeFilter) {
			if (this.shareStore && this.shareStore.canExport) {
				this.shareController.exportToPDF(this.glassContext, pageSize, includeFilter);
			}
		},

		_instrument: function _instrument(action, shareType) {
			var _this9 = this;

			if (this.instrumentationService && this.instrumentationService.enabled) {
				return this.shareController.getInstrumentation(this.glassContext).then(function (event) {
					event.action = action;
					event['custom.shareType'] = shareType;
					event.type = 'Shared Object';
					event.objectType = event.objectType || (_this9.object ? _this9.object.objectType : null);
					// If neither of those worked, try to get the type from glass.
					if (!event.objectType && typeof _this9.glassContext.getCurrentContentView === 'function') {
						event.objectType = _this9.glassContext.getCurrentContentView().getType();
					}
					event.milestoneName = action + '_' + event.objectType;
					_this9.instrumentationService.track(event);
				});
			}
			return Promise.resolve();
		},

		/**
   * Display errors as toast message.
   * @instance
   * @param {object} error
   * @throws {error}
   */
		_displayError: function _displayError(error) {
			var msg = error.message;
			if (error.connector) {
				var connectorType = error.connector.getType();
				var toastStringId = connectorType === 'email' ? 'toast_failure_email' : 'toast_failure';
				var toastMessageData = {
					connector: error.connector.getLabel(),
					error: msg
				};
				if (error.showContactAdmin) {
					toastStringId = connectorType === 'email' ? 'toast_failure_detailed_email' : 'toast_failure_detailed';
					toastMessageData.contactAdmin = StringResources.get('message_contact_administrator');
				}
				msg = StringResources.get(toastStringId, toastMessageData);
			}

			this.glassContext.showToast(msg, {
				type: 'error'
			});
			throw error;
		}
	});
	return ReactBridge;
});
;

var _temp = function () {
	if (typeof __REACT_HOT_LOADER__ === 'undefined') {
		return;
	}
}();

;

